import "@/App.css";
import { ViewTransition } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import Seo from "@/seo/Seo";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageProvider";
import { languageData } from "@/i18n/languages";
import { loadChoice } from "@/i18n/language";
import { lazyPage } from "@/lib/lazyPage";
import { useAgentTools } from "@/agent/useAgentTools";
import { routes } from "@/seo/routes";

// Chaque page vit dans son propre fichier JS, charge a la demande : l'accueil
// ne telecharge plus le code des 57 autres. Le pourquoi du chargeur maison, et
// l'absence voulue de <Suspense>, sont dans lib/lazyPage.js.
const LandingPage = lazyPage(() => import("@/pages/LandingPage"));
const LandingEn = lazyPage(() => import("@/pages/en/LandingEn"));
const LandingDe = lazyPage(() => import("@/pages/de/LandingDe"));
const BoardSetupEn = lazyPage(() => import("@/pages/en/BoardSetupEn"));
const BoardSetupDe = lazyPage(() => import("@/pages/de/BoardSetupDe"));
const RuleCricketEn = lazyPage(() => import("@/pages/en/RuleCricketEn"));
const RuleCricketDe = lazyPage(() => import("@/pages/de/RuleCricketDe"));
const Rule501En = lazyPage(() => import("@/pages/en/Rule501En"));
const Rule501De = lazyPage(() => import("@/pages/de/Rule501De"));
const Rule301En = lazyPage(() => import("@/pages/en/Rule301En"));
const Rule301De = lazyPage(() => import("@/pages/de/Rule301De"));
const RulesHubEn = lazyPage(() => import("@/pages/en/RulesHubEn"));
const RulesHubDe = lazyPage(() => import("@/pages/de/RulesHubDe"));
const TournamentEn = lazyPage(() => import("@/pages/en/TournamentEn"));
const TurnierDe = lazyPage(() => import("@/pages/de/TurnierDe"));
const LegalNoticeEn = lazyPage(() => import("@/pages/en/LegalNoticeEn"));
const ImpressumDe = lazyPage(() => import("@/pages/de/ImpressumDe"));
const PrivacyEn = lazyPage(() => import("@/pages/en/PrivacyEn"));
const DatenschutzDe = lazyPage(() => import("@/pages/de/DatenschutzDe"));
const TermsEn = lazyPage(() => import("@/pages/en/TermsEn"));
const NutzungsbedingungenDe = lazyPage(() => import("@/pages/de/NutzungsbedingungenDe"));
const ImproveEn = lazyPage(() => import("@/pages/en/blog/ImproveEn"));
const BesserWerfenDe = lazyPage(() => import("@/pages/de/blog/BesserWerfenDe"));
const DrillsEn = lazyPage(() => import("@/pages/en/blog/DrillsEn"));
const UebungenDe = lazyPage(() => import("@/pages/de/blog/UebungenDe"));
const CheckoutsGuideEn = lazyPage(() => import("@/pages/en/blog/CheckoutsGuideEn"));
const CheckoutsVerstehenDe = lazyPage(() => import("@/pages/de/blog/CheckoutsVerstehenDe"));
const AverageEn = lazyPage(() => import("@/pages/en/blog/AverageEn"));
const AverageDe = lazyPage(() => import("@/pages/de/blog/AverageDe"));
const DoublesTipsEn = lazyPage(() => import("@/pages/en/blog/DoublesTipsEn"));
const DoppelTippsDe = lazyPage(() => import("@/pages/de/blog/DoppelTippsDe"));
const TrainingPlanEn = lazyPage(() => import("@/pages/en/blog/TrainingPlanEn"));
const TrainingsplanDe = lazyPage(() => import("@/pages/de/blog/TrainingsplanDe"));
const Home = lazyPage(() => import("@/pages/Home"));
const GamePage = lazyPage(() => import("@/pages/GamePage"));
const RulesPage = lazyPage(() => import("@/pages/RulesPage"));
const Rule501Page = lazyPage(() => import("@/pages/rules/Rule501Page"));
const Rule301Page = lazyPage(() => import("@/pages/rules/Rule301Page"));
const RuleCricketPage = lazyPage(() => import("@/pages/rules/RuleCricketPage"));
const OrganiserTournoiPage = lazyPage(() => import("@/pages/OrganiserTournoiPage"));
const DistanceHauteurPage = lazyPage(() => import("@/pages/DistanceHauteurPage"));
const ArticleCommentProgresser = lazyPage(() => import("@/pages/blog/ArticleCommentProgresser"));
const ArticleMeilleursExercices = lazyPage(() => import("@/pages/blog/ArticleMeilleursExercices"));
const ArticleCheckoutsDarts = lazyPage(() => import("@/pages/blog/ArticleCheckoutsDarts"));
const ArticleAverageDarts = lazyPage(() => import("@/pages/blog/ArticleAverageDarts"));
const ArticleTipsDoubles = lazyPage(() => import("@/pages/blog/ArticleTipsDoubles"));
const ArticleProgrammeEntrainement = lazyPage(() => import("@/pages/blog/ArticleProgrammeEntrainement"));
const MentionsLegalesPage = lazyPage(() => import("@/pages/MentionsLegalesPage"));
const ConfidentialitePage = lazyPage(() => import("@/pages/ConfidentialitePage"));
const CguPage = lazyPage(() => import("@/pages/CguPage"));
const NotFound = lazyPage(() => import("@/pages/NotFound"));
const FaqHub = lazyPage(() => import("@/pages/faq/FaqHub"));
const BlogIndex = lazyPage(() => import("@/pages/blog/BlogIndex"));
const MultiplayerPage = lazyPage(() => import("@/pages/multiplayer/MultiplayerPage"));
const CheckoutChart = lazyPage(() => import("@/pages/checkout/CheckoutChart"));
const CheckoutScorePage = lazyPage(() => import("@/pages/checkout-score/CheckoutScorePage"));
const ScoringAppPage = lazyPage(() => import("@/pages/scoring-app/ScoringAppPage"));
const StatsPage = lazyPage(() => import("@/pages/stats/StatsPage"));
const TrainingPage = lazyPage(() => import("@/pages/training/TrainingPage"));

/**
 * Les 507 pages de score (169 restes, trois langues), produites par
 * seo/scorePages.js. Une route exacte par page plutot qu'un parametre : un
 * reste hors table — /fr/table-checkouts/171, /fr/table-checkouts/007 — tombe ainsi sur la
 * 404 comme n'importe quelle URL morte. La page pose elle-meme son <Seo> : elle
 * seule calcule son titre.
 */
const SCORE_ROUTES = Object.entries(routes).filter(([, meta]) => meta.checkoutScore !== undefined);

/**
 * Wraps a route element with its per-path SEO tags (title/description/canonical).
 *
 * Les donnees structurees, elles, sont ecrites dans le HTML au build par
 * scripts/prerender-seo.js : les injecter aussi au runtime produisait deux
 * FAQPage sur la meme page.
 */
function Page({ path, children }) {
    return (
        <>
            <Seo path={path} />
            {children}
        </>
    );
}

/**
 * La racine n'a plus de page a elle : le francais vit sous /fr comme l'anglais
 * sous /en. Caddy redirige deja / cote serveur, par domaine — le .fr vers /fr,
 * le .com vers /en. Ce composant ne sert que la navigation interne et le retour
 * arriere du navigateur, et il suit la langue de l'interface : un joueur
 * anglais qui revient a l'accueil depuis sa partie doit retrouver l'accueil
 * anglais, pas le francais que la racine envoyait a tout le monde.
 */
function RootRedirect() {
    const { lang } = useLanguage();
    // Au chargement a froid de /, le fournisseur ne connait pas encore le choix
    // memorise : il le lit dans un effet, et <Navigate> navigue dans le sien,
    // qui part avant. Lu ici, le choix l'emporte sur la langue du navigateur.
    // Cas rare — sur les domaines publics Caddy repond a la racine avant React —
    // mais reel sur le domaine interne et en developpement.
    const choice = loadChoice();
    return <Navigate to={languageData(choice || lang).home} replace />;
}

/**
 * Les outils du site pour les assistants IA (src/agent) : creer une partie,
 * suggerer un checkout, planifier un tournoi. Rien ne se charge dans un
 * navigateur sans WebMCP.
 *
 * Pose en dernier enfant de l'arbre, et il ne rend rien : place avant, il
 * decalerait les identifiants que useId genere pour ses freres au prerendu.
 */
const loadSiteTools = () => import("@/agent/site").then((module) => module.default);

function SiteAgentTools() {
    const navigate = useNavigate();
    useAgentTools(loadSiteTools, { navigate });
    return null;
}

/**
 * L'arbre de routes, separe du routeur : le navigateur le monte sous
 * BrowserRouter, le build le rend sous StaticRouter (scripts/ssr.cjs).
 *
 * ViewTransition fait fondre l'ancienne page dans la nouvelle a chaque
 * navigation : BrowserRouter applique ses changements d'etat dans une
 * Transition React, ce qui suffit a React pour capturer l'avant et l'apres et
 * demander l'animation au navigateur. La duree et le respect de
 * prefers-reduced-motion sont dans index.css ; un navigateur sans l'API
 * change de page comme avant.
 *
 * Deux consequences a connaitre. React differe les effets passifs du commit
 * de navigation a la fin du fondu : ce qui doit etre en place avant la capture
 * de la nouvelle page — le defilement, la fermeture du menu — passe par un
 * effet de layout. Et au prerendu le composant n'emet aucun balisage mais
 * compte dans l'arbre : les identifiants que useId genere en aval se
 * decalent, ce que six pages montrent au build.
 */
export function AppRoutes() {
    return (
        <LanguageProvider>
            <ScrollToTop />
            <ViewTransition>
            <Routes>
                    <Route path="/fr" element={<Page path="/fr"><LandingPage /></Page>} />
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/en" element={<Page path="/en"><LandingEn /></Page>} />
                    <Route path="/de" element={<Page path="/de"><LandingDe /></Page>} />
                    <Route path="/en/dartboard-height-and-distance" element={<Page path="/en/dartboard-height-and-distance"><BoardSetupEn /></Page>} />
                    <Route path="/de/dart-abstand-und-hoehe" element={<Page path="/de/dart-abstand-und-hoehe"><BoardSetupDe /></Page>} />
                    <Route path="/en/cricket-darts-rules" element={<Page path="/en/cricket-darts-rules"><RuleCricketEn /></Page>} />
                    <Route path="/de/cricket-dart-regeln" element={<Page path="/de/cricket-dart-regeln"><RuleCricketDe /></Page>} />
                    <Route path="/en/501-darts-rules" element={<Page path="/en/501-darts-rules"><Rule501En /></Page>} />
                    <Route path="/de/501-dart-regeln" element={<Page path="/de/501-dart-regeln"><Rule501De /></Page>} />
                    <Route path="/en/301-darts-rules" element={<Page path="/en/301-darts-rules"><Rule301En /></Page>} />
                    <Route path="/de/301-dart-regeln" element={<Page path="/de/301-dart-regeln"><Rule301De /></Page>} />
                    <Route path="/en/darts-rules" element={<Page path="/en/darts-rules"><RulesHubEn /></Page>} />
                    <Route path="/de/dart-regeln" element={<Page path="/de/dart-regeln"><RulesHubDe /></Page>} />
                    <Route path="/new" element={<Page path="/new"><Home /></Page>} />
                    {/* Live game pages are private/dynamic → noindex (no SEO config) */}
                    <Route path="/game/:gameId" element={<><Seo path="/game" noindex /><GamePage /></>} />
                    <Route path="/fr/regles" element={<Page path="/fr/regles"><RulesPage /></Page>} />
                    <Route path="/fr/regle-501" element={<Page path="/fr/regle-501"><Rule501Page /></Page>} />
                    <Route path="/fr/regle-301" element={<Page path="/fr/regle-301"><Rule301Page /></Page>} />
                    <Route path="/fr/regle-cricket" element={<Page path="/fr/regle-cricket"><RuleCricketPage /></Page>} />
                    <Route path="/fr/multijoueur" element={<Page path="/fr/multijoueur"><MultiplayerPage /></Page>} />
                    <Route path="/en/play-darts-with-friends" element={<Page path="/en/play-darts-with-friends"><MultiplayerPage /></Page>} />
                    <Route path="/de/dart-zu-mehreren-spielen" element={<Page path="/de/dart-zu-mehreren-spielen"><MultiplayerPage /></Page>} />
                    <Route path="/fr/table-checkouts" element={<Page path="/fr/table-checkouts"><CheckoutChart /></Page>} />
                    <Route path="/en/darts-checkout-chart" element={<Page path="/en/darts-checkout-chart"><CheckoutChart /></Page>} />
                    <Route path="/de/dart-checkout-tabelle" element={<Page path="/de/dart-checkout-tabelle"><CheckoutChart /></Page>} />
                    {SCORE_ROUTES.map(([path, meta]) => (
                        <Route
                            key={path}
                            path={path}
                            element={<CheckoutScorePage score={meta.checkoutScore} />}
                        />
                    ))}
                    <Route path="/fr/faq" element={<Page path="/fr/faq"><FaqHub /></Page>} />
                    <Route path="/en/faq" element={<Page path="/en/faq"><FaqHub /></Page>} />
                    <Route path="/de/faq" element={<Page path="/de/faq"><FaqHub /></Page>} />
                    <Route path="/fr/app-score-flechette" element={<Page path="/fr/app-score-flechette"><ScoringAppPage /></Page>} />
                    <Route path="/en/darts-scoring-app" element={<Page path="/en/darts-scoring-app"><ScoringAppPage /></Page>} />
                    <Route path="/de/dart-zaehler-app" element={<Page path="/de/dart-zaehler-app"><ScoringAppPage /></Page>} />
                    <Route path="/fr/stats-darts" element={<Page path="/fr/stats-darts"><StatsPage /></Page>} />
                    <Route path="/en/darts-statistics" element={<Page path="/en/darts-statistics"><StatsPage /></Page>} />
                    <Route path="/de/dart-statistiken" element={<Page path="/de/dart-statistiken"><StatsPage /></Page>} />
                    <Route path="/fr/entrainement-darts" element={<Page path="/fr/entrainement-darts"><TrainingPage /></Page>} />
                    <Route path="/en/darts-practice" element={<Page path="/en/darts-practice"><TrainingPage /></Page>} />
                    <Route path="/de/dart-training" element={<Page path="/de/dart-training"><TrainingPage /></Page>} />
                    <Route path="/fr/organiser-tournoi-flechettes" element={<Page path="/fr/organiser-tournoi-flechettes"><OrganiserTournoiPage /></Page>} />
                    <Route path="/en/run-a-darts-tournament" element={<Page path="/en/run-a-darts-tournament"><TournamentEn /></Page>} />
                    <Route path="/de/dart-turnier-ausrichten" element={<Page path="/de/dart-turnier-ausrichten"><TurnierDe /></Page>} />
                    <Route path="/fr/distance-hauteur-cible-flechettes" element={<Page path="/fr/distance-hauteur-cible-flechettes"><DistanceHauteurPage /></Page>} />
                    <Route path="/fr/blog" element={<Page path="/fr/blog"><BlogIndex /></Page>} />
                    <Route path="/en/darts-blog" element={<Page path="/en/darts-blog"><BlogIndex /></Page>} />
                    <Route path="/de/dart-blog" element={<Page path="/de/dart-blog"><BlogIndex /></Page>} />
                    <Route path="/en/blog/how-to-improve-at-darts" element={<Page path="/en/blog/how-to-improve-at-darts"><ImproveEn /></Page>} />
                    <Route path="/de/blog/besser-dart-werfen" element={<Page path="/de/blog/besser-dart-werfen"><BesserWerfenDe /></Page>} />
                    <Route path="/en/blog/best-darts-drills" element={<Page path="/en/blog/best-darts-drills"><DrillsEn /></Page>} />
                    <Route path="/de/blog/beste-dart-uebungen" element={<Page path="/de/blog/beste-dart-uebungen"><UebungenDe /></Page>} />
                    <Route path="/en/blog/darts-checkouts-guide" element={<Page path="/en/blog/darts-checkouts-guide"><CheckoutsGuideEn /></Page>} />
                    <Route path="/de/blog/dart-checkouts-verstehen" element={<Page path="/de/blog/dart-checkouts-verstehen"><CheckoutsVerstehenDe /></Page>} />
                    <Route path="/en/blog/improve-your-darts-average" element={<Page path="/en/blog/improve-your-darts-average"><AverageEn /></Page>} />
                    <Route path="/de/blog/dart-average-verbessern" element={<Page path="/de/blog/dart-average-verbessern"><AverageDe /></Page>} />
                    <Route path="/en/blog/hitting-doubles-tips" element={<Page path="/en/blog/hitting-doubles-tips"><DoublesTipsEn /></Page>} />
                    <Route path="/de/blog/doppel-treffen-tipps" element={<Page path="/de/blog/doppel-treffen-tipps"><DoppelTippsDe /></Page>} />
                    <Route path="/en/blog/darts-training-plan" element={<Page path="/en/blog/darts-training-plan"><TrainingPlanEn /></Page>} />
                    <Route path="/de/blog/dart-trainingsplan" element={<Page path="/de/blog/dart-trainingsplan"><TrainingsplanDe /></Page>} />
                    <Route path="/fr/blog/comment-progresser-darts" element={<Page path="/fr/blog/comment-progresser-darts"><ArticleCommentProgresser /></Page>} />
                    <Route path="/fr/blog/meilleurs-exercices-darts" element={<Page path="/fr/blog/meilleurs-exercices-darts"><ArticleMeilleursExercices /></Page>} />
                    <Route path="/fr/blog/checkouts-au-darts" element={<Page path="/fr/blog/checkouts-au-darts"><ArticleCheckoutsDarts /></Page>} />
                    <Route path="/fr/blog/comment-ameliorer-average" element={<Page path="/fr/blog/comment-ameliorer-average"><ArticleAverageDarts /></Page>} />
                    <Route path="/fr/blog/tips-doubles" element={<Page path="/fr/blog/tips-doubles"><ArticleTipsDoubles /></Page>} />
                    <Route path="/fr/blog/routine-entrainement" element={<Page path="/fr/blog/routine-entrainement"><ArticleProgrammeEntrainement /></Page>} />
                    <Route path="/fr/mentions-legales" element={<Page path="/fr/mentions-legales"><MentionsLegalesPage /></Page>} />
                    <Route path="/en/legal-notice" element={<Page path="/en/legal-notice"><LegalNoticeEn /></Page>} />
                    <Route path="/de/rechtliche-hinweise" element={<Page path="/de/rechtliche-hinweise"><ImpressumDe /></Page>} />
                    <Route path="/fr/confidentialite" element={<Page path="/fr/confidentialite"><ConfidentialitePage /></Page>} />
                    <Route path="/en/privacy" element={<Page path="/en/privacy"><PrivacyEn /></Page>} />
                    <Route path="/de/datenschutz" element={<Page path="/de/datenschutz"><DatenschutzDe /></Page>} />
                    <Route path="/fr/cgu" element={<Page path="/fr/cgu"><CguPage /></Page>} />
                    <Route path="/en/terms-of-use" element={<Page path="/en/terms-of-use"><TermsEn /></Page>} />
                    <Route path="/de/nutzungsbedingungen" element={<Page path="/de/nutzungsbedingungen"><NutzungsbedingungenDe /></Page>} />
                    <Route path="*" element={<NotFound />} />
            </Routes>
            </ViewTransition>
            <SiteAgentTools />
        </LanguageProvider>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors />
        </div>
    );
}

export default App;
